import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StyleConfiguration, ReferenceImage } from '../../models/narrative.model';
import { NARRATIVE_CONSTANTS } from '../../constants/narrative.constants';
import { VideoSettingsCameraOnlyComponent } from '../../../shared/video-settings-camera-only/video-settings-camera-only.component';
import { VideoSettingsCameraOnly, VIDEO_SIZES } from '../../../../models/video-settings.model';
import { NarrativeHelper } from '../../utils/narrative-helper';
import { CustomModelService } from '../../../../services/custom-model.service';
import { CustomModel } from '../../../../models/custom-model.model';
import { NarrativeErrorHandlerService } from '../../services/narrative-error-handler.service';
import { GoogleDriveImageSelectorComponent } from '../../../shared/google-drive-image-selector/google-drive-image-selector.component';
import { GoogleDriveImage } from '../../../../services/google-drive-images.service';
import { ToastService } from '../../../../services/toast.service';
import { I18nService } from '../../../../i18n/i18n.service';

@Component({
  selector: 'app-narrative-style-config',
  standalone: true,
  imports: [CommonModule, FormsModule, GoogleDriveImageSelectorComponent, VideoSettingsCameraOnlyComponent],
  templateUrl: './narrative-style-config.component.html',
  styleUrls: ['./narrative-style-config.component.scss']
})
export class NarrativeStyleConfigComponent implements OnInit, OnDestroy {
  @Input() config!: StyleConfiguration;
  @Output() configChange = new EventEmitter<StyleConfiguration>();
  @Output() styleImageUploaded = new EventEmitter<ReferenceImage>();
  @Output() styleImageRemoved = new EventEmitter<void>();
  @Output() validationError = new EventEmitter<string>();

  readonly constants = NARRATIVE_CONSTANTS.STYLE;
  readonly errorMessages = NARRATIVE_CONSTANTS.ERROR_MESSAGES;

  isDragging = false;
  isUploading = false;

  // カスタムモデル関連
  customModels: CustomModel[] = [];
  isLoadingCustomModels = false;
  private destroy$ = new Subject<void>();

  // Google Drive画像選択関連
  googleDriveSelectorOpen = false;

  // 動画サイズ設定
  selectedVideoSize: string = '540p';

  // 動画設定（カメラモーションのみ）
  videoSettingsCameraOnly: VideoSettingsCameraOnly = {
    cameraMotion: ''
  };

  // サイズオプション
  videoSizeOptions = Object.entries(VIDEO_SIZES).map(([key, value]) => ({
    value: key,
    label: value.label
  }));

  constructor(
    public customModelService: CustomModelService,
    private errorHandler: NarrativeErrorHandlerService,
    private toastService: ToastService,
    public i18n: I18nService
  ) { }

  ngOnInit(): void {
    // スタイルモデル一覧を監視（trainingMode="Style"のみ）
    this.customModelService.styleModels$
      .pipe(takeUntil(this.destroy$))
      .subscribe(models => {
        this.customModels = models;

        const shouldAutoSelect = this.config.type === 'custom'
          && !this.config.customModelId
          && models.length > 0;

        if (shouldAutoSelect) {
          this.onCustomModelChange(models[0].assetId);
        }
      });

    // ローディング状態を監視
    this.customModelService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoading => {
        this.isLoadingCustomModels = isLoading;
      });

    // configからvideoSettingsを初期化
    if (this.config.videoSettings) {
      this.selectedVideoSize = this.config.videoSettings.selectedSize || '540p';
      this.videoSettingsCameraOnly = {
        cameraMotion: this.config.videoSettings.cameraMotion || ''
      };
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onStyleTypeChange(type: 'image' | 'custom'): void {
    // カスタムモデル選択時にモデル一覧を読み込み
    if (type === 'custom' && this.customModels.length === 0 && !this.isLoadingCustomModels) {
      this.loadCustomModels();
    }

    // タイプが変更された場合、カスタムモデル関連の設定をリセット
    const updatedConfig = {
      ...this.config,
      type
    };

    if (type === 'image') {
      // 画像参照モードに変更された場合、カスタムモデル設定をクリア
      updatedConfig.customModelId = undefined;
      updatedConfig.modelVersion = undefined;
    }

    this.configChange.emit(updatedConfig);
  }

  onStrengthChange(strength: number): void {
    this.configChange.emit({
      ...this.config,
      strength
    });
  }

  onVisualIntensityChange(intensity: number): void {
    this.configChange.emit({
      ...this.config,
      visualIntensity: intensity
    });
  }

  onCustomModelChange(modelId: string | null): void {
    // カスタムモデル未読み込みの場合は読み込み
    if (this.customModels.length === 0 && !this.isLoadingCustomModels) {
      this.loadCustomModels();
    }

    let modelVersion: string | undefined = undefined;

    // カスタムモデルが選択された場合
    if (modelId) {
      const selectedModel = this.customModelService.getModelById(modelId);
      if (selectedModel) {
        // ベースモデルからmodelVersionを取得
        modelVersion = this.customModelService.getModelVersion(selectedModel);
      }
    }

    this.configChange.emit({
      ...this.config,
      customModelId: modelId || undefined,
      modelVersion
    });
  }

  loadCustomModels(): void {
    this.customModelService.loadCustomModels({
      sortBy: 'modifiedDate',
      limit: 50,
      publishedState: 'published'
    }).pipe(takeUntil(this.destroy$)).subscribe({
      error: (error) => {
        this.errorHandler.handleError(error, {
          type: 'validation',
          operation: 'カスタムモデル読み込み'
        });
      }
    });
  }

  triggerStyleFileInput(): void {
    const input = document.getElementById('styleFileInput') as HTMLInputElement;
    input?.click();
  }

  onStyleFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.processStyleFiles(input.files);
    }
  }

  onStyleDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onStyleDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onStyleDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files?.length) {
      this.processStyleFiles(files);
    }
  }

  removeStyleImage(): void {
    this.styleImageRemoved.emit();
  }

  formatFileSize(size: number): string {
    return NarrativeHelper.formatFileSize(size);
  }

  onVideoSizeChange(size: string): void {
    this.selectedVideoSize = size;
    this.updateVideoSettings();
  }

  onVideoSettingsCameraOnlyChange(settings: VideoSettingsCameraOnly): void {
    this.videoSettingsCameraOnly = settings;
    this.updateVideoSettings();
  }

  private updateVideoSettings(): void {
    const updatedConfig = {
      ...this.config,
      videoSettings: {
        selectedSize: this.selectedVideoSize,
        cameraMotion: this.videoSettingsCameraOnly.cameraMotion,
        promptStyle: '',
        shotAngle: '',
        shotSize: ''
      }
    };
    this.configChange.emit(updatedConfig);
  }

  private processStyleFiles(files: FileList): void {
    const file = files[0];

    if (!NarrativeHelper.isImageFile(file)) {
      this.validationError.emit(this.i18n.t('vnm.err.fileTypeInvalid'));
      return;
    }

    if (!NarrativeHelper.isFileSizeValid(file)) {
      this.validationError.emit(this.i18n.t('vnm.err.fileSizeTooLarge'));
      return;
    }

    this.isUploading = true;

    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      const referenceImage: ReferenceImage = {
        file,
        name: file.name,
        size: file.size,
        preview: e.target?.result as string
      };

      this.styleImageUploaded.emit(referenceImage);
      this.isUploading = false;
    };

    reader.onerror = () => {
      this.isUploading = false;
      this.validationError.emit(this.i18n.t('vnm.err.fileReadFailed'));
    };

    reader.readAsDataURL(file);
  }

  // Google Drive画像選択関連メソッド
  openGoogleDriveSelector(): void {
    this.googleDriveSelectorOpen = true;
  }

  onGoogleDriveImageSelected(event: { file: File; imageData: GoogleDriveImage }): void {
    const { file, imageData } = event;

    // FileListを作成して processStyleFiles に渡す
    const fileList = this.createFileList([file]);
    this.processStyleFiles(fileList);

    this.toastService.showToast(
      this.i18n.t('toast.styleRefSelected', { prompt: imageData.prompt.substring(0, 30) }),
      'success'
    );
  }

  private createFileList(files: File[]): FileList {
    const dt = new DataTransfer();
    files.forEach(file => dt.items.add(file));
    return dt.files;
  }

  onGoogleDriveSelectorClosed(): void {
    this.googleDriveSelectorOpen = false;
  }
}